import { setGADataLayer } from 'analytics'
import { SyntheticEvent, useContext, useState } from 'react'
import DotsLoader from 'shared/components/loaders/dots'
import UserContext from 'shared/contexts/user'
import styled from 'styled-components'
import { ALL_ANIMATED_MIME_TYPES } from '../../../constants'
import { CreationToolsContext } from '../../../manager'
import CreationToolError from '../../../types/error'
import CreationToolOutputType from '../../../types/output-type'
import { getUrlMimeType } from '../../../utils/get-source-info'
import { validateSourceByInput } from '../../../utils/validate-source'
import { WelcomeBlock } from '../../blocks'
import { Subtitle, Text, TextInput } from '../../typography'
import { CreationToolsWelcomeContext } from './../context'
import { GiphyIcon, VimeoIcon, YoutubeIcon } from './icons'

const Wrapper = styled(WelcomeBlock)`
    position: relative;
`

const Description = styled(Text as any)`
    max-width: 100%;
`

const getMappedUrl = (source: string): string => {
    const mimeType = getUrlMimeType(source)

    // valid source
    if (mimeType) {
        return source
    }

    // giphy urls
    if (source.indexOf(`giphy.com`) > -1) {
        const isClip = source.indexOf('/clips/') > -1
        const filename = isClip ? `giphy360p.mp4` : `giphy.gif`
        const path = source.split('?')[0].split('/').pop()
        const gifId = path ? path.split('-').pop() : ''
        return gifId ? `https://media.giphy.com/media/${gifId}/${filename}` : source
    }

    return ''
}

const CreationWelcomeUrlInput = () => {
    const { setFiles, setShowLoginModal } = useContext(CreationToolsContext)
    const { setError, setErrorSource, setIsLoading } = useContext(CreationToolsWelcomeContext)
    const [isValidating, setIsValidating] = useState<boolean>(false)
    const { user } = useContext(UserContext)

    const onInput = async (e: SyntheticEvent<HTMLInputElement, Event>) => {
        const { value = '' } = e.currentTarget
        const url = getMappedUrl(value)

        if (url) {
            const content = {
                contentFormat: 'gif',
                contentType: 'url',
            }
            setIsValidating(true)
            setIsLoading(true)

            if (!user.loggedIn) {
                setShowLoginModal(true)
                return
            }

            setGADataLayer({
                event: 'choose_file_click',
                options: {
                    options: {
                        form: {
                            formName: 'choose file',
                        },
                    },
                    content,
                },
            })
            try {
                const validatedFile = await validateSourceByInput(url, ALL_ANIMATED_MIME_TYPES)
                setFiles([validatedFile], CreationToolOutputType.Gif)
                setIsValidating(false)

                setGADataLayer({
                    event: 'choose_file_success',
                    options: {
                        options: {
                            form: {
                                formName: 'choose file',
                            },
                        },
                        content,
                    },
                })
            } catch (err) {
                setError(err as CreationToolError)
                setErrorSource(value)
                setGADataLayer({
                    event: 'choose_file_error',
                    options: {
                        form: {
                            formName: 'choose file',
                            formError: CreationToolError.gaLabel(err as CreationToolError, url),
                        },
                        content,
                    },
                })
                setIsValidating(false)
                setIsLoading(true)
            }
        }
    }

    return (
        <Wrapper>
            <div style={isValidating ? { opacity: 0, pointerEvents: 'none' } : {}}>
                <Subtitle as="h3">Any URL</Subtitle>
                <Description>We support media URLs from GIPHY, YouTube, Vimeo, &amp; many others!</Description>
                <TextInput>
                    <input type="url" placeholder="Paste any media or GIF URL" onInput={onInput} />
                    <GiphyIcon />
                    <YoutubeIcon />
                    <VimeoIcon />
                </TextInput>
            </div>
            {isValidating && <DotsLoader isAbsolute />}
        </Wrapper>
    )
}

export default CreationWelcomeUrlInput
